import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Grow,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIndicatorIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  HOME_STYLES,
  applyQuickAccessOrder,
  generateDynamicCards,
  getVisibleQuickAccessCards,
  moveQuickAccessPath,
  sortCardsByPopularity,
} from '../constants/homeConstants';
import usePermissions from '../../hooks/usePermissions';
import { useMostVisitedPages } from '../../hooks/usePageTracking';
import useQuickAccessPreferencesStore from '../../stores/useQuickAccessPreferencesStore';

const QuickAccessSection = () => {
  const navigate = useNavigate();
  const isSingleColumnLayout = useMediaQuery('(max-width:599.95px)');
  const { hasModule, hasAnyModule, hasAllModules } = usePermissions();
  const { mostVisitedPages, hasSufficientData } = useMostVisitedPages();
  const { pinnedPaths, orderedPaths, togglePinnedPath, setOrderedPaths } =
    useQuickAccessPreferencesStore();
  const [expanded, setExpanded] = useState(false);
  const [draggedPath, setDraggedPath] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const suppressClickAfterDragRef = useRef(false);
  const cardRefs = useRef(new Map());

  const dynamicCards = useMemo(
    () => generateDynamicCards({ hasModule, hasAnyModule, hasAllModules }, mostVisitedPages),
    [hasModule, hasAnyModule, hasAllModules, mostVisitedPages]
  );

  const baseCards = useMemo(
    () => (hasSufficientData ? sortCardsByPopularity(dynamicCards) : dynamicCards),
    [dynamicCards, hasSufficientData]
  );

  const cardsToShow = useMemo(
    () => applyQuickAccessOrder(baseCards, orderedPaths),
    [baseCards, orderedPaths]
  );

  const INITIAL_CARDS_COUNT = 8;

  const collapsedVisibleCards = getVisibleQuickAccessCards(
    cardsToShow,
    pinnedPaths,
    INITIAL_CARDS_COUNT
  );
  const visibleCards = expanded ? cardsToShow : collapsedVisibleCards;
  const hasMoreCards = cardsToShow.length > collapsedVisibleCards.length;
  const hiddenCardsCount = cardsToShow.length - collapsedVisibleCards.length;
  const hasCustomPreferences = pinnedPaths.length > 0 || orderedPaths.length > 0;
  const cardsCountText = expanded
    ? 'Mostrando todas'
    : `Mostrando ${visibleCards.length} de ${cardsToShow.length}`;

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleTogglePinned = (event, path) => {
    event.stopPropagation();
    togglePinnedPath(path);
  };

  const handleDragStart = (event, path) => {
    event.stopPropagation();
    suppressClickAfterDragRef.current = true;
    setDraggedPath(path);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', path);

    const cardNode = cardRefs.current.get(path);
    if (cardNode && event.dataTransfer.setDragImage) {
      const rect = cardNode.getBoundingClientRect();
      event.dataTransfer.setDragImage(cardNode, rect.width / 2, Math.min(rect.height / 2, 80));
    }
  };

  const clearDragState = () => {
    setDraggedPath(null);
    setDropTarget(null);
    setTimeout(() => {
      suppressClickAfterDragRef.current = false;
    }, 150);
  };

  const getDropPosition = (event, element) => {
    const rect = element.getBoundingClientRect();

    if (isSingleColumnLayout) {
      return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
    }

    return event.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
  };

  const handleDragOver = (event, path) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (draggedPath === path) {
      setDropTarget(null);
      return;
    }

    if (draggedPath && draggedPath !== path) {
      const position = getDropPosition(event, event.currentTarget);
      setDropTarget({ path, position });
    }
  };

  const handleDrop = (event, targetPath) => {
    event.preventDefault();
    event.stopPropagation();

    const sourcePath = event.dataTransfer.getData('text/plain') || draggedPath;
    if (!sourcePath || sourcePath === targetPath) {
      clearDragState();
      return;
    }

    const position = getDropPosition(event, event.currentTarget);
    const currentOrder = cardsToShow.map((card) => card.path);
    const nextOrder = moveQuickAccessPath(
      currentOrder,
      sourcePath,
      targetPath,
      position === 'after'
    );

    setOrderedPaths(nextOrder);
    clearDragState();
  };

  const handleDragEnd = () => {
    clearDragState();
  };

  const handleCardClick = (path) => {
    if (suppressClickAfterDragRef.current) return;
    navigate(path);
  };

  const handleDragHandleKeyDown = (event, path) => {
    const moveOffsetByKey = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
    };
    const offset = moveOffsetByKey[event.key];
    if (!offset) return;

    const currentOrder = cardsToShow.map((card) => card.path);
    const currentIndex = currentOrder.indexOf(path);
    const nextIndex = currentIndex + offset;
    if (currentIndex === -1 || nextIndex < 0 || nextIndex >= currentOrder.length) return;

    event.preventDefault();
    event.stopPropagation();

    const nextOrder = [...currentOrder];
    const [movedPath] = nextOrder.splice(currentIndex, 1);
    nextOrder.splice(nextIndex, 0, movedPath);
    setOrderedPaths(nextOrder);
  };

  const setCardRef = (path) => (node) => {
    if (node) {
      cardRefs.current.set(path, node);
      return;
    }

    cardRefs.current.delete(path);
  };

  const getDescriptionText = () => {
    if (hasCustomPreferences) return 'Páginas configuradas para acceso rápido';
    return hasSufficientData
      ? 'Páginas ordenadas por frecuencia de uso'
      : 'Páginas disponibles según tus permisos';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: 'text.primary' }}>
        Acceso Rápido
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {getDescriptionText()}
        {hasMoreCards && (
          <span style={{ marginLeft: '8px', opacity: 0.7 }}>
            ({cardsCountText})
          </span>
        )}
      </Typography>

      <Grid container spacing={3}>
        {visibleCards.map((card, index) => {
          const isPinned = pinnedPaths.includes(card.path);
          const isDragging = draggedPath === card.path;
          const isDropTarget = dropTarget?.path === card.path && draggedPath !== card.path;
          const showBeforeIndicator = isDropTarget && dropTarget?.position === 'before';
          const showAfterIndicator = isDropTarget && dropTarget?.position === 'after';

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={card.path}>
              <Grow in timeout={800 + index * 200}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                  {(showBeforeIndicator || showAfterIndicator) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        zIndex: 3,
                        pointerEvents: 'none',
                        bgcolor: 'primary.main',
                        borderRadius: '999px',
                        boxShadow: '0 0 0 3px rgba(255, 255, 255, 0.95)',
                        ...(isSingleColumnLayout
                          ? {
                              left: 8,
                              right: 8,
                              height: 4,
                              top: showBeforeIndicator ? -10 : 'auto',
                              bottom: showAfterIndicator ? -10 : 'auto',
                            }
                          : {
                              top: 8,
                              bottom: 8,
                              width: 4,
                              left: showBeforeIndicator ? -10 : 'auto',
                              right: showAfterIndicator ? -10 : 'auto',
                            }),
                      }}
                    />
                  )}

                  <Card
                    ref={setCardRef(card.path)}
                    sx={{
                      ...HOME_STYLES.card,
                      background: card.color,
                      position: 'relative',
                      cursor: draggedPath ? 'default' : 'pointer',
                      opacity: isDragging ? 0.35 : 1,
                      transform: isDragging ? 'scale(0.98)' : 'translateY(0px)',
                      boxShadow: isDragging ? '0 12px 30px rgba(0,0,0,0.18)' : undefined,
                      '&:hover': draggedPath
                        ? {
                            transform: isDragging ? 'scale(0.98)' : 'translateY(0px)',
                            boxShadow: isDragging
                              ? '0 12px 30px rgba(0,0,0,0.18)'
                              : '0 8px 24px rgba(0,0,0,0.16)',
                          }
                        : HOME_STYLES.card['&:hover'],
                      '&:hover .quick-access-drag-handle': {
                        opacity: 1,
                      },
                    }}
                    onClick={() => handleCardClick(card.path)}
                    onDragOver={(event) => handleDragOver(event, card.path)}
                    onDrop={(event) => handleDrop(event, card.path)}
                    onDragEnd={handleDragEnd}
                  >
                    <CardContent sx={{ p: 3, pb: 5, textAlign: 'center', '&:last-child': { pb: 5 } }}>
                    {card.visitCount > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '12px',
                          px: 1,
                          py: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {card.visitCount} {card.visitCount === 1 ? 'visita' : 'visitas'}
                      </Box>
                    )}

                    <Tooltip title={isPinned ? 'Quitar fijado' : 'Fijar acceso'}>
                      <IconButton
                        size="small"
                        aria-label={
                          isPinned
                            ? `Quitar ${card.title} de acceso rápido`
                            : `Fijar ${card.title} en acceso rápido`
                        }
                        aria-pressed={isPinned}
                        onClick={(event) => handleTogglePinned(event, card.path)}
                        onMouseDown={(event) => event.stopPropagation()}
                        onDragStart={(event) => event.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 9,
                          right: 10,
                          width: 30,
                          height: 30,
                          bgcolor: isPinned
                            ? 'rgba(255, 255, 255, 0.95)'
                            : 'rgba(255, 255, 255, 0.18)',
                          color: isPinned ? 'primary.main' : 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            color: 'primary.main',
                          },
                        }}
                      >
                        {isPinned ? (
                          <PushPinIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <PushPinOutlinedIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Arrastrar para ordenar">
                      <IconButton
                        className="quick-access-drag-handle"
                        size="small"
                        draggable
                        aria-label={`Reordenar ${card.title}`}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onKeyDown={(event) => handleDragHandleKeyDown(event, card.path)}
                        onDragStart={(event) => handleDragStart(event, card.path)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          position: 'absolute',
                          right: 10,
                          bottom: 10,
                          width: 30,
                          height: 30,
                          opacity: isDragging || isDropTarget ? 1 : 0.72,
                          bgcolor: isDragging
                            ? 'rgba(255, 255, 255, 0.95)'
                            : 'rgba(255, 255, 255, 0.16)',
                          color: isDragging ? 'primary.main' : 'white',
                          cursor: isDragging ? 'grabbing' : 'grab',
                          touchAction: 'none',
                          transition: 'opacity 0.2s ease, background-color 0.2s ease',
                          '&:hover': {
                            opacity: 1,
                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                            color: 'primary.main',
                          },
                          '&:active': {
                            cursor: 'grabbing',
                          },
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>

                    <Box sx={{ mb: 2, pt: 1 }}>
                      {React.cloneElement(card.icon, { sx: { fontSize: 48, color: 'white' } })}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                  </Card>
                </Box>
              </Grow>
            </Grid>
          );
        })}
      </Grid>

      {hasMoreCards && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleToggleExpand}
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
              },
              px: 4,
              py: 1.5,
            }}
          >
            {expanded ? 'Mostrar menos' : `Ver ${hiddenCardsCount} más`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuickAccessSection;
